import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, DatePicker, DropdownButton, DropdownButtonContext, FlexBox, Tooltip, } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import moment from 'moment-timezone';
import { useEffect, useMemo, useRef, useState } from 'react';
import { analytics } from 'app/utils/segment';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { notificationActions } from 'components/notifications/actions/notification';
import { followUpModalActions } from 'components/shared/followUpModal/actions/followUpModal';
import { useShortHumanReadableTimer } from 'hooks/useShortHumanReadableTimer';
import { useCommandHandler } from 'utils/commands';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import styles from './conversationFollowUp.module.scss';
import { useNextQuarter } from './utils/useNextQuarter';
export const ConversationFollowUp = () => {
    const { direction, id: csid = null, status, queue, assignment, pending_expiration_at: pendingExpirationAt, } = useAppSelector((state) => state.conversation.metaConversation.conversation);
    const dispatch = useAppDispatch();
    const followUpLocalTime = useMemo(() => pendingExpirationAt ? moment(pendingExpirationAt).utc().local() : null, [pendingExpirationAt]);
    const relativeFollowUpTime = useShortHumanReadableTimer(followUpLocalTime);
    const shouldShowRemoveFollowup = pendingExpirationAt &&
        (status === 'pending' || status === 'awaitingpending');
    const nextQuarter = useNextQuarter();
    const followUpLocalTimeOrNextQuarter = followUpLocalTime || nextQuarter;
    const [selectedDate, setSelectedDate] = useState(followUpLocalTimeOrNextQuarter);
    const dropdownButtonRef = useRef(null);
    const isPresetsEnabled = useBreakpointValue({ base: false, md: true });
    useCommandHandler({
        name: 'conversation:followUp',
        actionFn: () => {
            var _a;
            (_a = dropdownButtonRef.current) === null || _a === void 0 ? void 0 : _a.handleDropdownToggle();
        },
    });
    useEffect(() => {
        setSelectedDate(followUpLocalTimeOrNextQuarter);
    }, [followUpLocalTimeOrNextQuarter]);
    const presets = [
        {
            key: '1_hour',
            label: 'Remind me in 1 hour',
            date: moment().add(1, 'hour'),
        },
        {
            key: '3_hours',
            label: 'Remind me in 3 hours',
            date: moment().add(3, 'hours'),
        },
        {
            key: '24_hours',
            label: 'Remind me in 24 hours',
            date: moment().add(24, 'hours'),
        },
        {
            key: '48_hours',
            label: 'Remind me in 48 hours',
            date: moment().add(48, 'hours'),
        },
    ];
    const handleDateChange = (toggleDropdown) => ({ date, presetKey }) => {
        analytics.conversationFollowUpMenuOptionClicked({
            option: presetKey !== null && presetKey !== void 0 ? presetKey : 'date',
        });
        setSelectedDate(date);
        // If a preset is not selected, wait for confirm button
        if (!presetKey || !csid) {
            return;
        }
        performFollowUp(csid, date, toggleDropdown);
    };
    const handleConfirm = (toggleDropdown) => () => {
        analytics.conversationFollowUpMenuOptionClicked({
            option: 'confirm',
        });
        if (!csid) {
            return;
        }
        performFollowUp(csid, selectedDate, toggleDropdown);
    };
    const performFollowUp = (csid, date, toggleDropdown) => {
        var _a, _b, _c;
        if (((_a = date === null || date === void 0 ? void 0 : date.diff(moment())) !== null && _a !== void 0 ? _a : 0) < 0) {
            dispatch(notificationActions.notificationAdd({
                text: `You should not select a time that has passed.`,
                title: 'Invalid time',
                showDesktop: true,
                icon: 'exclamation',
                status: 'danger',
                time: 5000,
            }));
            return;
        }
        const timestamp = (_b = date === null || date === void 0 ? void 0 : date.utc().format()) !== null && _b !== void 0 ? _b : null;
        if (direction === 'outbound' && !(queue === null || queue === void 0 ? void 0 : queue.id)) {
            dispatch(followUpModalActions.followUpModalOpen({
                csid,
                conversationQueueId: (_c = queue === null || queue === void 0 ? void 0 : queue.id) !== null && _c !== void 0 ? _c : null,
                timestamp,
                queueSelection: true,
            }));
        }
        else {
            analytics.conversationFollowUp({ conversationId: csid });
            dispatch(conversationActions.conversationToPending(Object.assign({ csid,
                timestamp }, (direction === 'outbound' && { queueId: queue === null || queue === void 0 ? void 0 : queue.id }))));
        }
        // setTimeout is needed because react-dates which is used in the DatePicker
        // has to update before toggling the dropdown, otherwise, it'll disappear
        setTimeout(toggleDropdown, 1);
    };
    const cancelFollowUp = (toggleDropdown) => () => {
        analytics.conversationFollowUpMenuOptionClicked({
            option: 'cancel',
        });
        dispatch(conversationActions.conversationCancelPending({ csid }));
        toggleDropdown();
    };
    const tooltipContent = (_jsx(CommandKeybindingsTooltipContent, Object.assign({ commandName: "conversation:followUp" }, { children: followUpLocalTime ? 'Reschedule follow up' : 'Schedule a follow up' })));
    return (_jsx(Tooltip, Object.assign({ dark: true, content: tooltipContent, disabled: assignment === null }, { children: _jsx(DropdownButton, Object.assign({ ref: dropdownButtonRef, "data-testid": "conversation-follow-up-dropdown", icon: "calendar", buttonProps: { disabled: assignment === null, variant: 'secondary' }, onToggle: (isOpen) => isOpen && analytics.conversationFollowUpMenuOpened(), dropdownProps: {
                className: styles.dropdown,
                autoFocus: true,
            }, dropdown: _jsx(DropdownButtonContext.Consumer, { children: ({ toggleDropdown }) => (_jsxs(_Fragment, { children: [_jsx(DatePicker, { date: selectedDate, onChange: handleDateChange(toggleDropdown), withDateInput: true, withTimePicker: true, inputsPlacement: "top", minDate: moment(), presets: isPresetsEnabled ? presets === null || presets === void 0 ? void 0 : presets.slice() : undefined, timeRoundingStrategy: "minute" }), _jsxs(FlexBox, Object.assign({ justifyContent: "flex-end", gap: sizes.spacer[250], padding: sizes.spacer[250], className: styles.actions }, { children: [shouldShowRemoveFollowup && (_jsx(Button, Object.assign({ variant: "tertiary", onClick: cancelFollowUp(toggleDropdown) }, { children: "Remove follow up" }))), _jsx(Button, Object.assign({ variant: "primary", disabled: !selectedDate, onClick: handleConfirm(toggleDropdown) }, { children: "Confirm" }))] }))] })) }) }, { children: followUpLocalTime ? `Follow up ${relativeFollowUpTime}` : 'Follow up' })) })));
};
